import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import InventoryIcon from "@mui/icons-material/Inventory";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import RemoveShoppingCartIcon from "@mui/icons-material/RemoveShoppingCart";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import SellIcon from "@mui/icons-material/Sell";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import GridViewIcon from "@mui/icons-material/GridView";
import { useCompany } from "../../components/context/CompanyContext";
import Loading from "../../components/loading/loading";
import { titleSetRef } from "../../redux/titleRedux";
import { userRequest } from "../../requestMethods";
import { effectiveStories } from "../../util/storeScope";
import { canAccessPath } from "../../util/accessControl";
import "../../App.css";

const BRAND = "#10B981";

function KpiCard({ icon, title, heroValue, sub, gradient, onClick, cta }) {
  const [hover, setHover] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => onClick && setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        background: `linear-gradient(135deg, ${gradient[0]}, ${gradient[1]})`,
        borderRadius: 16,
        overflow: "hidden",
        cursor: onClick ? "pointer" : "default",
        transition: "transform 0.18s, box-shadow 0.18s",
        transform: hover ? "translateY(-3px)" : "none",
        boxShadow: hover ? "0 10px 28px rgba(0,0,0,0.20)" : "0 3px 10px rgba(0,0,0,0.10)",
        position: "relative",
      }}
    >
      <div style={{ position: "absolute", right: -24, top: -24, width: 110, height: 110, borderRadius: "50%", background: "rgba(255,255,255,0.10)", pointerEvents: "none" }} />
      <div style={{ position: "absolute", right: 16, bottom: -32, width: 80, height: 80, borderRadius: "50%", background: "rgba(255,255,255,0.06)", pointerEvents: "none" }} />
      <div style={{ padding: "18px 20px 14px", position: "relative" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
          <span style={{ fontSize: 10, fontWeight: 800, color: "rgba(255,255,255,0.80)", textTransform: "uppercase", letterSpacing: "0.09em", fontFamily: "Manrope" }}>{title}</span>
          {icon && (
            <div style={{ width: 34, height: 34, borderRadius: 10, background: "rgba(255,255,255,0.20)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              {icon}
            </div>
          )}
        </div>
        <div style={{ fontSize: 26, fontWeight: 900, color: "#fff", fontFamily: "Manrope", letterSpacing: "-0.02em", lineHeight: 1.1 }}>{heroValue}</div>
        <div style={{ fontSize: 11, color: "rgba(255,255,255,0.70)", fontFamily: "Manrope", marginTop: 5 }}>{sub}</div>
      </div>
      <div style={{ borderTop: "1px solid rgba(255,255,255,0.15)", padding: "9px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", background: "rgba(0,0,0,0.10)" }}>
        <span style={{ fontSize: 11, color: "rgba(255,255,255,0.85)", fontFamily: "Manrope", fontWeight: 600 }}>{cta || "Ver detalhes"}</span>
        <span style={{ fontSize: 14, color: "rgba(255,255,255,0.65)" }}>›</span>
      </div>
    </div>
  );
}

function BucketCard({ label, count, hint, onClick }) {
  const [hover, setHover] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => onClick && setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        background: "#fff",
        border: "1px solid #E5E7EB",
        borderRadius: 12,
        padding: "16px 18px",
        textAlign: "center",
        cursor: onClick ? "pointer" : "default",
        transition: "transform 0.15s, box-shadow 0.15s",
        transform: hover ? "translateY(-2px)" : "none",
        boxShadow: hover ? "0 4px 12px rgba(0,0,0,0.10)" : "0 1px 4px rgba(0,0,0,0.05)",
      }}
    >
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 4 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: "#6B7280", fontFamily: "Manrope", textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</span>
        {onClick && <OpenInNewIcon style={{ fontSize: 12, color: "#9CA3AF" }} />}
      </div>
      <div style={{ fontSize: 22, fontWeight: 900, color: "#111827", fontFamily: "Manrope", margin: "8px 0 4px" }}>{count}</div>
      {hint && <div style={{ fontSize: 11, color: "#9CA3AF", fontFamily: "Manrope" }}>{hint}</div>}
    </div>
  );
}

// Card de navegação no estilo do IndicadoresNav (Vendas, Vend. Estratégica...).
function NavReportCard({ label, sub, icon, onClick, active = false }) {
  const [hover, setHover] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        background: active ? `linear-gradient(135deg, ${BRAND}, #E8502A)` : "#fff",
        border: `1.5px solid ${active || hover ? BRAND : "#E5E7EB"}`,
        borderRadius: 12,
        padding: "14px 18px",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        gap: 12,
        transition: "box-shadow 0.15s, transform 0.15s",
        transform: hover && !active ? "translateY(-2px)" : "none",
        boxShadow: active
          ? "0 4px 16px rgba(238,130,83,0.35)"
          : hover
            ? "0 4px 14px rgba(238,130,83,0.25)"
            : "0 1px 4px rgba(0,0,0,0.05)",
      }}
    >
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: 8,
          background: active ? "rgba(255,255,255,0.2)" : "#FEF3ED",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {icon}
      </div>
      <div>
        <div style={{ fontSize: 13, fontWeight: 800, color: active ? "#fff" : "#111827", fontFamily: "Manrope", lineHeight: 1.2 }}>{label}</div>
        <div style={{ fontSize: 11, color: active ? "rgba(255,255,255,0.8)" : "#9CA3AF", fontFamily: "Manrope", marginTop: 2 }}>{sub}</div>
      </div>
    </div>
  );
}

function ChartCard({ title, sub, children }) {
  return (
    <div style={{
      background: "#fff",
      borderRadius: 14,
      border: "1px solid #E5E7EB",
      boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
      padding: "18px 18px 14px",
    }}>
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#111827", fontFamily: "Manrope" }}>{title}</div>
        {sub && <div style={{ fontSize: 11, color: "#9CA3AF", fontFamily: "Manrope", marginTop: 2 }}>{sub}</div>}
      </div>
      {children}
    </div>
  );
}

export default function IndicadoresProdutos() {
  const [prdBestSeller, setPrdBestSeller] = useState(null);
  const [prdNounSeller, setPrdNounSeller] = useState(null);
  const [prdAccess, setPrdAccess] = useState(null);
  const [withoutOrder, setWithoutOrder] = useState([]);
  const [stock, setStock] = useState(null);
  const [premium, setPremium] = useState([]);
  const [ordersQuantity, setOrdersQuantity] = useState([]);
  const [avgOrdersQuantity, setAvgOrdersQuantity] = useState([]);
  const [stories, setStories] = useState([]);
  const [selected, setSelected] = useState([]);
  const storeIds = useMemo(() => selected.map((s) => s.value), [selected]);
  const [dataIni, setDateIni] = useState(() => dayjs().add(-7, "day").format("YYYY-MM-DD"));
  const [dataFim, setDateFim] = useState(() => dayjs().format("YYYY-MM-DD"));
  const [readonly, setReadonly] = useState("7");
  const [isLoading, setIsLoading] = useState(true);

  const currentUser = useSelector((state) => state.user.currentUser);
  // Loja selecionada → leva para a ProductList (estoque por loja = grids.storeId),
  // para que a contagem do card e a lista batam também com filtro de loja.
  const storeQSIds = effectiveStories(currentUser, storeIds);
  const storeQS = storeQSIds.length > 0 ? `&storeId=${storeQSIds.join(",")}` : "";
  const { companyId, slug, accessControl } = useCompany();
  const nav = useNavigate();
  // Cards de relatório negados no controle de acesso (3º nível) somem.
  const canSee = (route) =>
    canAccessPath(accessControl, currentUser?.position, `/${slug}/${route}`);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(titleSetRef({ title: "Indicadores Produtos" }));
    window.scrollTo(0, 0);
    if (!currentUser) nav("../login");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (companyId > 0 && currentUser) {
      getStories();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  useEffect(() => {
    if (companyId > 0 && currentUser) {
      setIsLoading(true);
      carregarTudo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, dataIni, dataFim, storeIds]);

  const buildObj = () => ({
    start: dataIni,
    end: dataFim,
    stories: effectiveStories(currentUser, storeIds),
  });

  const getStories = async () => {
    try {
      const res = await userRequest.get(`${companyId}/store/stories`);
      const list = res.data.map((item) => ({ label: item.store, value: item.storeId }));
      setStories(list);
      if (currentUser?.position === "Gerente") {
        const userStore = list.find((s) => s.value === currentUser.storeId);
        if (userStore) setSelected([userStore]);
      }
    } catch {}
  };

  async function carregarTudo() {
    try {
      await Promise.all([
        getBestSeller(),
        getNounSeller(),
        getWithoutOrder(),
        getProductAcess(),
        getStock(),
        getPremium(),
        getOrdersQuantity(),
        getAvgOrdersQuantity(),
      ]);
    } finally {
      setIsLoading(false);
    }
  }

  const getBestSeller = async () => {
    try {
      const res = await userRequest.post(`${companyId}/reportOrders/productsBestSeller`, buildObj());
      setPrdBestSeller(res.data);
    } catch {}
  };

  const getNounSeller = async () => {
    try {
      const res = await userRequest.post(`${companyId}/reportOrders/productsNounSeller`, buildObj());
      setPrdNounSeller(res.data);
    } catch {}
  };

  const getWithoutOrder = async () => {
    try {
      const res = await userRequest.post(`${companyId}/reportProducts/withoutOrder`, buildObj());
      setWithoutOrder(Array.isArray(res.data) ? res.data : []);
    } catch {}
  };

  const getProductAcess = async () => {
    try {
      const res = await userRequest.post(`${companyId}/events/productacess`, buildObj());
      setPrdAccess(res.data);
    } catch {}
  };

  const getStock = async () => {
    try {
      const res = await userRequest.post(`${companyId}/reportProducts/prodstock`, { stories: effectiveStories(currentUser, storeIds) });
      setStock(res.data);
    } catch {}
  };

  const getPremium = async () => {
    try {
      const res = await userRequest.post(`${companyId}/reportProducts/premium`, { stories: effectiveStories(currentUser, storeIds) });
      setPremium(Array.isArray(res.data) ? res.data : []);
    } catch {}
  };

  const getOrdersQuantity = async () => {
    try {
      const res = await userRequest.post(`${companyId}/reportOrders/userquantity`, buildObj());
      setOrdersQuantity(res.data || []);
    } catch {}
  };

  const getAvgOrdersQuantity = async () => {
    try {
      const res = await userRequest.post(`${companyId}/reportOrders/avgordersusequantity`, buildObj());
      setAvgOrdersQuantity(res.data || []);
    } catch {}
  };

  const handleChange = (e) => {
    const v = e.target.value;
    setReadonly(v);
    switch (v) {
      case "0": setDateIni(dayjs().format("YYYY-MM-DD")); setDateFim(dayjs().format("YYYY-MM-DD")); break;
      case "7": setDateIni(dayjs().add(-7, "day").format("YYYY-MM-DD")); setDateFim(dayjs().format("YYYY-MM-DD")); break;
      case "15": setDateIni(dayjs().add(-15, "day").format("YYYY-MM-DD")); setDateFim(dayjs().format("YYYY-MM-DD")); break;
      case "30": setDateIni(dayjs().add(-30, "day").format("YYYY-MM-DD")); setDateFim(dayjs().format("YYYY-MM-DD")); break;
      default: break;
    }
  };

  const periodLabel =
    readonly === "F" ? "filtro personalizado" : readonly === "0" ? "hoje" : `últimos ${readonly} dias`;

  return (
    <div style={{ padding: "16px 16px 48px 4px", fontFamily: "Manrope", background: "#F9FAFB", minHeight: "100vh" }}>

      {/* Cards de relatório — acima do filtro (estilo IndicadoresNav) */}
      <div style={{ display: "grid", gridAutoFlow: "column", gridAutoColumns: "1fr", gap: 12, marginBottom: 20 }}>
        <NavReportCard
          label="Produto"
          sub="Indicadores Geral"
          active
          icon={<Inventory2Icon style={{ fontSize: 20, color: "#fff" }} />}
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        />
        {canSee("relatorio-produto-venda") && (
          <NavReportCard
            label="Produto com Venda"
            sub="Relatório de produtos vendidos"
            icon={<SellIcon style={{ fontSize: 20, color: BRAND }} />}
            onClick={() => nav(`/${slug}/relatorio-produto-venda`)}
          />
        )}
        {canSee("relatorio-avise") && (
          <NavReportCard
            label="Avise-me quando chegar"
            sub="Relatório de produtos aguardados"
            icon={<NotificationsActiveIcon style={{ fontSize: 20, color: BRAND }} />}
            onClick={() => nav(`/${slug}/relatorio-avise`)}
          />
        )}
        {canSee("relatorio-grade-incompleta") && (
          <NavReportCard
            label="Grade Incompleto"
            sub="Relatório de grade incompleta"
            icon={<GridViewIcon style={{ fontSize: 20, color: BRAND }} />}
            onClick={() => nav(`/${slug}/relatorio-grade-incompleta`)}
          />
        )}
        {canSee("relatorio-estoque") && (
          <NavReportCard
            label="Estoque"
            sub="Relatório de estoque"
            icon={<WarehouseIcon style={{ fontSize: 20, color: BRAND }} />}
            onClick={() => nav(`/${slug}/relatorio-estoque`)}
          />
        )}
      </div>

      {/* Header + filtros */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20, justifyContent: "space-between" }}>
        <div>
          <span style={{ fontSize: 18, fontWeight: 700, color: "#111827" }}>Indicadores Produtos</span>
          <span style={{ fontSize: 12, color: "#9CA3AF", marginLeft: 8 }}>{periodLabel}</span>
        </div>
        <div style={{ display: "flex", alignItems: "flex-end", gap: 12 }}>

          {/* Filtro Lojas */}
          {stories.length > 0 && (() => {
            const isGerente = currentUser?.position === "Gerente";
            return (
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 4 }}>Lojas</div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", border: "1px solid #E5E7EB", borderRadius: 8, padding: "6px 8px", background: "#fff" }}>
                  <label
                    style={{
                      cursor: isGerente ? "default" : "pointer",
                      padding: "3px 10px",
                      borderRadius: 6,
                      border: `1px solid ${selected.length === 0 ? BRAND : "#E5E7EB"}`,
                      background: selected.length === 0 ? BRAND : "transparent",
                      color: selected.length === 0 ? "#fff" : "#6B7280",
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                    onClick={() => !isGerente && setSelected([])}
                  >
                    Todas
                  </label>
                  {stories.map((story) => {
                    const isActive = selected.some((s) => s.value === story.value);
                    return (
                      <label
                        key={story.value}
                        style={{
                          cursor: isGerente ? "default" : "pointer",
                          padding: "3px 10px",
                          borderRadius: 6,
                          border: `1px solid ${isActive ? BRAND : "#E5E7EB"}`,
                          background: isActive ? BRAND : "transparent",
                          color: isActive ? "#fff" : "#6B7280",
                          fontSize: 12,
                          fontWeight: 600,
                          opacity: isGerente ? 0.7 : 1,
                        }}
                        onClick={() => {
                          if (isGerente) return;
                          setSelected(isActive
                            ? selected.filter((s) => s.value !== story.value)
                            : [...selected, story]
                          );
                        }}
                      >
                        {story.label}
                      </label>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* Filtro Período */}
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 4 }}>Período</div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", border: "1px solid #E5E7EB", borderRadius: 8, padding: "6px 8px", background: "#fff" }}>
              {[
                { v: "30", l: "30 dias" },
                { v: "15", l: "15 dias" },
                { v: "7",  l: "7 dias"  },
                { v: "0",  l: "Hoje"    },
                { v: "F",  l: "Filtro"  },
              ].map(({ v, l }) => (
                <label
                  key={v}
                  style={{
                    cursor: "pointer",
                    padding: "3px 10px",
                    borderRadius: 6,
                    border: `1px solid ${readonly === v ? BRAND : "#E5E7EB"}`,
                    background: readonly === v ? BRAND : "transparent",
                    color: readonly === v ? "#fff" : "#6B7280",
                    fontSize: 12,
                    fontWeight: 600,
                  }}
                >
                  <input type="radio" value={v} checked={readonly === v} onChange={handleChange} style={{ display: "none" }} />
                  {l}
                </label>
              ))}
              {readonly === "F" && (
                <>
                  <div style={{ width: 1, background: "#E5E7EB", margin: "2px 2px" }} />
                  <input type="date" value={dataIni} onChange={(e) => setDateIni(e.target.value)} min="2023-01-01" style={{ fontSize: 12, border: "none", outline: "none" }} />
                  <span style={{ color: "#9CA3AF", fontSize: 12 }}>—</span>
                  <input type="date" value={dataFim} onChange={(e) => setDateFim(e.target.value)} min="2023-01-01" style={{ fontSize: 12, border: "none", outline: "none" }} />
                </>
              )}
            </div>
          </div>

        </div>
      </div>

      {isLoading ? (
        <Loading />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

          {/* KPIs hero */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 16 }}>
            <KpiCard
              icon={<TrendingUpIcon style={{ fontSize: 20, color: "#fff" }} />}
              title="Mais Vendido"
              heroValue={prdBestSeller?.ref || "-"}
              sub={`${prdBestSeller?.count || 0}x vendido no período`}
              gradient={["#10B981", "#059669"]}
              cta="Ver produtos com venda"
              onClick={() => nav(`/${slug}/products?withSales=1&salesDir=desc&start=${dataIni}&end=${dataFim}${storeQS}&from=indicadoresprodutos`)}
            />
            <KpiCard
              icon={<TrendingDownIcon style={{ fontSize: 20, color: "#fff" }} />}
              title="Menos Vendido"
              heroValue={prdNounSeller?.ref || "-"}
              sub={`${prdNounSeller?.count || 0}x vendido no período`}
              gradient={["#F59E0B", "#D97706"]}
              cta="Ver produtos com menor venda"
              onClick={() => nav(`/${slug}/products?withSales=1&salesDir=asc&start=${dataIni}&end=${dataFim}${storeQS}&from=indicadoresprodutos`)}
            />
            <KpiCard
              icon={<InventoryIcon style={{ fontSize: 20, color: "#fff" }} />}
              title="Mais Acessado"
              heroValue={prdAccess?.maiorRef?.ref || "-"}
              sub={`${prdAccess?.maiorRef?.count || 0}x acessado no período`}
              gradient={["#3B82F6", "#1D4ED8"]}
              cta="Ver produto"
              onClick={() => prdAccess?.maiorRef?.ref && nav(`/${slug}/products?ref=${prdAccess.maiorRef.ref}${storeQS}&from=indicadoresprodutos`)}
            />
            <KpiCard
              icon={<RemoveShoppingCartIcon style={{ fontSize: 20, color: "#fff" }} />}
              title="Sem Venda"
              heroValue={withoutOrder.length}
              sub="produtos sem pedido no período"
              gradient={["#EF4444", "#B91C1C"]}
              cta="Ver produtos sem venda"
              onClick={() => nav(`/${slug}/products?noSales=1&start=${dataIni}&end=${dataFim}&from=indicadoresprodutos${storeQS}`)}
            />
            <KpiCard
              icon={<WorkspacePremiumIcon style={{ fontSize: 20, color: "#fff" }} />}
              title="Produtos Premium"
              heroValue={premium.length}
              sub="produtos em pré-venda"
              gradient={["#7C3AED", "#5B21B6"]}
              cta="Ver produtos premium"
              onClick={() => nav(`/${slug}/products?presales=1${storeQS}&from=indicadoresprodutos`)}
            />
          </div>

          {/* Grade Completa */}
          <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #E5E7EB", boxShadow: "0 2px 8px rgba(0,0,0,0.06)", padding: "18px 20px 16px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: "#111827", fontFamily: "Manrope" }}>Grade Completa</span>
              <span style={{ fontSize: 11, color: "#9CA3AF", fontFamily: "Manrope" }}>antigüidade do produto</span>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 12 }}>
              <BucketCard label="Até 15 dias"       count={stock?.completed15     || 0} onClick={() => nav(`/${slug}/products?stockFilter=completed15${storeQS}&from=indicadoresprodutos`)} />
              <BucketCard label="16 a 30 dias"      count={stock?.completed15e30  || 0} onClick={() => nav(`/${slug}/products?stockFilter=completed15e30${storeQS}&from=indicadoresprodutos`)} />
              <BucketCard label="31 a 60 dias"      count={stock?.completed31e60  || 0} onClick={() => nav(`/${slug}/products?stockFilter=completed31e60${storeQS}&from=indicadoresprodutos`)} />
              <BucketCard label="Acima de 60 dias"  count={stock?.completed60     || 0} onClick={() => nav(`/${slug}/products?stockFilter=completed60${storeQS}&from=indicadoresprodutos`)} />
              <BucketCard label="Invis. c/ estoque" count={stock?.completedVisible || 0} onClick={() => nav(`/${slug}/products?stockFilter=completedVisible${storeQS}&from=indicadoresprodutos`)} />
            </div>
          </div>

          {/* Grade Incompleta */}
          <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #E5E7EB", boxShadow: "0 2px 8px rgba(0,0,0,0.06)", padding: "18px 20px 16px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: "#111827", fontFamily: "Manrope" }}>Grade Incompleta</span>
              <span
                onClick={() => nav(`/${slug}/products?stockFilter=visibleNoStock${storeQS}&from=indicadoresprodutos`)}
                style={{ fontSize: 12, color: BRAND, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}
              >
                Ver visível sem estoque <OpenInNewIcon style={{ fontSize: 13 }} />
              </span>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 12 }}>
              <BucketCard label="< 25 pçs"           count={stock?.m25               || 0} onClick={() => nav(`/${slug}/products?stockFilter=m25${storeQS}&from=indicadoresprodutos`)} />
              <BucketCard label="25 a 50 pçs"        count={stock?.m25e50            || 0} onClick={() => nav(`/${slug}/products?stockFilter=m25e50${storeQS}&from=indicadoresprodutos`)} />
              <BucketCard label="> 50 pçs"           count={stock?.m50               || 0} onClick={() => nav(`/${slug}/products?stockFilter=m50${storeQS}&from=indicadoresprodutos`)} />
              <BucketCard label="Visível s/ estoque" count={stock?.stockOver          || 0} onClick={() => nav(`/${slug}/products?stockFilter=visibleNoStock${storeQS}&from=indicadoresprodutos`)} />
              <BucketCard label="Invis. c/ estoque"  count={stock?.incompletedVisible || 0} onClick={() => nav(`/${slug}/products?stockFilter=incompletedVisible${storeQS}&from=indicadoresprodutos`)} />
            </div>
          </div>

          {/* Gráficos: Qtde por vendedor */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <ChartCard title="Quantidade por Vendedor(a)" sub={periodLabel}>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={ordersQuantity}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="managedBy" interval={0} angle={-40} tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="Quantidade" fill={BRAND} barSize={12} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
            <ChartCard title="Quantidade Média por Vendedor(a)" sub={periodLabel}>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={avgOrdersQuantity}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="managedBy" interval={0} angle={-40} tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="Quantidade" fill={BRAND} barSize={12} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>

        </div>
      )}
    </div>
  );
}
